<div class="off-hire-dialog">
  <div class="dialog-header">
    <div class="header-title">
      <mat-icon class="header-icon">pause_circle_filled</mat-icon>
      <div>
        <h2 class="title-text">Interruptions de facturation</h2>
        <span class="subtitle-text">{{ data.productName }} · ligne {{ data.lineId }}</span>
      </div>
    </div>
    <button type="button" class="btn-close" (click)="close()" aria-label="Fermer" matTooltip="Fermer">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content class="dialog-content">
    <div class="info-banner">
      <mat-icon class="banner-icon">info</mat-icon>
      <div class="banner-text">
        Chaque saisie est enregistrée tout de suite, datée et attribuée à son auteur. Une interruption ne s'efface pas : elle s'annule avec un motif et reste visible, barrée. La facturation applique la règle de la carte de location de la ligne.
      </div>
    </div>

    <div class="suspensions-section">
      <h3 class="section-title">
        <span>Interruptions de la ligne</span>
        <span class="count-badge">{{ interruptions.length }}</span>
      </h3>

      @if (interruptions.length === 0) {
        <div class="empty-state">
          <mat-icon class="empty-icon">event_available</mat-icon>
          <span>Aucune interruption enregistrée sur cette ligne.</span>
        </div>
      } @else {
        <div class="suspension-table-wrapper">
          <table class="suspension-table">
            <thead>
              <tr>
                <th>N°</th>
                <th>Statut</th>
                <th>Début</th>
                <th>Reprise</th>
                <th>Motif</th>
                <th>Commentaire</th>
                <th>Saisie</th>
                <th>Annulation</th>
                @if (canWrite) {
                  <th class="col-actions">Actions</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (item of interruptions; track item.id) {
                @let st = status(item);
                <tr [class.is-active]="st === 'running'" [class.is-cancelled]="st === 'cancelled'" [class.is-target]="targetId === item.id">
                  <td class="num-cell">
                    {{ item.id }}
                    @if (item.replaces) {
                      <span class="replaces-tag" [matTooltip]="'Remplace l\'interruption n° ' + item.replaces">⟲ {{ item.replaces }}</span>
                    }
                  </td>
                  <td>
                    @switch (st) {
                      @case ('running') {
                        <span class="status-badge active" matTooltip="La facturation est suspendue aujourd'hui"><span class="pulse-dot"></span>En cours</span>
                      }
                      @case ('upcoming') {
                        <span class="status-badge upcoming">À venir</span>
                      }
                      @case ('over') {
                        <span class="status-badge closed">Terminée</span>
                      }
                      @case ('cancelled') {
                        <span class="status-badge cancelled">Annulée</span>
                      }
                    }
                  </td>
                  <td class="struck">{{ item.start ? frDay(startDay(item)) : 'Dès la livraison' }}</td>
                  <td class="struck">
                    @if (item.end) {
                      {{ frDay(billedAgainOn(item)) }}
                    } @else {
                      <span class="text-muted">ouverte</span>
                    }
                  </td>
                  <td class="struck"><span class="reason-tag">{{ reasonName(item.reason_id) }}</span></td>
                  <td class="comment-cell struck" [matTooltip]="item.comment || ''">{{ item.comment || '—' }}</td>
                  <td class="stamp-cell">
                    <div>{{ frInstant(item.created.dat) }}</div>
                    <div class="text-muted">{{ userName(item.created.id_user) }}</div>
                  </td>
                  <td class="stamp-cell">
                    @if (item.cancelled; as c) {
                      <div>{{ frInstant(c.dat) }} · {{ userName(c.id_user) }}</div>
                      <div class="cancel-reason" [matTooltip]="c.reason">{{ c.reason }}</div>
                    } @else {
                      <span class="text-muted">—</span>
                    }
                  </td>
                  @if (canWrite) {
                    <td class="col-actions">
                      @if (!item.cancelled) {
                        <div class="action-buttons">
                          @if (st === 'running' && !item.end) {
                            <button mat-icon-button type="button" class="action-btn resume-btn" matTooltip="Reprise aujourd'hui (remplace l'entrée)" [disabled]="busy" (click)="editInterruption(item, yesterday)">
                              <mat-icon>play_circle_outline</mat-icon>
                            </button>
                          }
                          <button mat-icon-button type="button" class="action-btn edit-btn" matTooltip="Modifier (annule et remplace l'entrée)" [disabled]="busy" (click)="editInterruption(item)">
                            <mat-icon>edit</mat-icon>
                          </button>
                          <button mat-icon-button type="button" class="action-btn delete-btn" matTooltip="Annuler (l'entrée reste visible, barrée)" [disabled]="busy" (click)="cancelInterruption(item)">
                            <mat-icon>block</mat-icon>
                          </button>
                        </div>
                      }
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>

    @if (canWrite) {
      <div class="form-container">
        <h3 class="section-title">
          @switch (mode) {
            @case ('add') { <span>Ajouter une interruption</span> }
            @case ('replace') { <span>Modifier l'interruption n° {{ targetId }} (elle sera annulée et remplacée)</span> }
            @case ('cancel') { <span>Annuler l'interruption n° {{ targetId }}</span> }
          }
        </h3>

        @if (savedNotice; as s) {
          <div class="info-banner fse-notice saved-notice">
            <mat-icon class="banner-icon">campaign</mat-icon>
            <div class="banner-text">Interruption n° {{ s.id ?? '?' }} enregistrée. {{ s.text }}</div>
          </div>
        }

        <form [formGroup]="form" (ngSubmit)="submit()" class="suspension-form">
          @if (mode !== 'cancel') {
            @if (noticeMissing) {
              <div class="error-notice notice-missing">
                <mat-icon>error_outline</mat-icon>
                <span>Dossier facturé par FSE : aucun motif d'interruption de c_values {{ reasonsGroup }} ne porte data.fse_notice, le rappel d'avis à la caisse (convention PSDM, art. 30 §2) n'est pas paramétré.</span>
              </div>
            }
            @if (reasonsState === 'missing') {
              <div class="error-notice">
                <mat-icon>error_outline</mat-icon>
                <span>Aucun motif d'interruption actif dans c_values {{ reasonsGroup }} : migrate_c_values_42_motifs_location.sql à jouer.</span>
              </div>
            } @else if (reasonsState === 'error') {
              <div class="error-notice">
                <mat-icon>error_outline</mat-icon>
                <span>Lecture des motifs (c_values {{ reasonsGroup }}) impossible.</span>
              </div>
            }
            <div class="dates-selection-grid">
              <mat-checkbox formControlName="fromDelivery" color="primary" [matTooltip]="data.deliveredDay ? 'Livraison du ' + frDay(data.deliveredDay) : 'Ligne pas encore livrée : la facturation démarrera à la reprise'">Dès la livraison</mat-checkbox>
              @if (!form.value.fromDelivery) {
                <div class="date-col">
                  <app-select-date label="Début (premier jour non facturé)" [date]="startDate" (dateChange)="onStartDateChange($event)"></app-select-date>
                </div>
              }
              <div class="date-col end-col">
                <div class="end-field-container">
                  <app-select-date label="Fin (dernier jour non facturé, inclus)" [date]="endDate" (dateChange)="onEndDateChange($event)"></app-select-date>
                  @if (endDate) {
                    <button type="button" class="clear-end-btn" matTooltip="Sans dernier jour : interruption ouverte" aria-label="Effacer le dernier jour" (click)="clearEndDate()">
                      <mat-icon>clear</mat-icon>
                    </button>
                  }
                </div>
              </div>
            </div>

            <div class="fields-row">
              <mat-form-field appearance="outline" class="reason-field">
                <mat-label>Motif</mat-label>
                <mat-select formControlName="reason_id">
                  @for (r of reasons; track r.id) {
                    <mat-option [value]="r.id">{{ r.name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline" class="comment-field">
                <mat-label>Commentaire</mat-label>
                <input matInput formControlName="comment" />
              </mat-form-field>
            </div>
            @if (pendingNotice; as n) {
              <div class="info-banner fse-notice pending-notice">
                <mat-icon class="banner-icon">campaign</mat-icon>
                <div class="banner-text">{{ n }}</div>
              </div>
            }
          }

          @if (mode !== 'add') {
            <div class="fields-row">
              <mat-form-field appearance="outline" class="cancel-field">
                <mat-label>Motif de l'annulation (obligatoire)</mat-label>
                <input matInput formControlName="cancel_reason" />
              </mat-form-field>
            </div>
          }

          @if (form.hasError('invertedDates')) {
            <div class="error-notice">
              <mat-icon>error_outline</mat-icon>
              <span>Le dernier jour non facturé ne peut précéder le début.</span>
            </div>
          }
          @if (errorMsg) {
            <div class="error-notice">
              <mat-icon>error_outline</mat-icon>
              <span>{{ errorMsg }}</span>
            </div>
          }

          <div class="form-actions">
            @if (mode !== 'add') {
              <button mat-button type="button" [disabled]="busy" (click)="resetForm()">Abandonner</button>
            }
            <button mat-flat-button color="primary" type="submit" [disabled]="busy || form.invalid">
              <mat-icon>{{ mode === 'add' ? 'add' : mode === 'replace' ? 'check' : 'block' }}</mat-icon>
              <span>{{ mode === 'add' ? 'Enregistrer l\'interruption' : mode === 'replace' ? 'Enregistrer la modification' : 'Annuler l\'interruption' }}</span>
            </button>
          </div>
        </form>
      </div>
    } @else if (!data.readonly) {
      <div class="info-banner">
        <mat-icon class="banner-icon">save</mat-icon>
        <div class="banner-text">Ligne pas encore enregistrée : enregistrez le dossier, puis rouvrez cette fenêtre pour saisir une interruption.</div>
      </div>
    }
  </mat-dialog-content>

  <mat-dialog-actions align="end" class="dialog-actions">
    <button mat-flat-button color="primary" type="button" [disabled]="busy" (click)="close()">
      <mat-icon>done</mat-icon>
      <span>Fermer</span>
    </button>
  </mat-dialog-actions>
</div>
